import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Copy, Check, Share2, Download, MessageCircle, Instagram, Twitter } from "lucide-react";

/**
 * ShareStoreModal — lets a seller invite customers to join their sale.
 * The link points at /join/:store_id, a smart resolver that opens the
 * store's LIVE stream when one is running, otherwise the store page.
 * Offers copy, native share sheet, WhatsApp / Instagram / X quick shares,
 * and a downloadable QR code for packaging / in-person.
 */
export default function ShareStoreModal({ store, onClose }) {
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState("");
  const qrRef = useRef(null);

  const link = `${window.location.origin}/join/${store.store_id}`;
  const shareText = `Shop live with ${store.name} on ShopRizz`;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2400); };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      flash("Couldn't copy — long-press the link to copy manually");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: store.name, text: shareText, url: link }); } catch {}
    } else {
      copy();
      flash("Link copied!");
    }
  };

  const openWhatsApp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${link}`)}`, "_blank", "noopener");

  const openX = () =>
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}`, "_blank", "noopener");

  const openInstagram = async () => {
    try { await navigator.clipboard.writeText(link); } catch {}
    flash("Link copied — paste it in your Instagram story or DM");
    setTimeout(() => window.open("https://www.instagram.com/", "_blank", "noopener"), 700);
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${store.name.replace(/\s+/g, "-").toLowerCase()}-shoprizz-qr.png`;
    a.click();
  };

  const socials = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, onClick: openWhatsApp, color: "#25D366" },
    { key: "instagram", label: "Instagram", icon: Instagram, onClick: openInstagram, color: "#E1306C" },
    { key: "x", label: "X", icon: Twitter, onClick: openX, color: "#FFFFFF" },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-store-modal-overlay"
    >
      <div
        className="w-full max-w-md bg-[#0F0817] border border-[#3D2845] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-store-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#3D2845]">
          <div className="flex items-center gap-2">
            <Share2 size={18} className="text-[#D4FF00]" />
            <span className="font-display font-bold uppercase text-sm tracking-wide">Share your store</span>
          </div>
          <button onClick={onClose} data-testid="share-store-close-btn" className="p-1.5 rounded-full hover:bg-[#261534] transition" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-sm text-[#A1A1AA]">
            Send this link to your customers. If you're <span className="text-[#FF007F] font-bold">live</span>, it opens your sale instantly — otherwise it opens <span className="text-white font-semibold">{store.name}</span>.
          </p>

          {/* Link + copy */}
          <div className="flex items-center gap-2 bg-[#261534] border border-[#3D2845] rounded-full pl-4 pr-1.5 py-1.5">
            <span className="flex-1 min-w-0 truncate text-sm text-[#A1A1AA]" data-testid="share-store-link">{link}</span>
            <button
              onClick={copy}
              data-testid="share-store-copy-btn"
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition ${copied ? "bg-[#D4FF00] text-black" : "bg-[#3D2845] text-white hover:bg-[#4a3254]"}`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Native share */}
          <button
            onClick={nativeShare}
            data-testid="share-store-native-btn"
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            <Share2 size={16} /> Share link
          </button>

          {/* Social quick shares */}
          <div className="grid grid-cols-3 gap-2">
            {socials.map((s) => (
              <button
                key={s.key}
                onClick={s.onClick}
                data-testid={`share-store-${s.key}-btn`}
                className="flex flex-col items-center gap-2 py-3 rounded-xl bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition"
              >
                <s.icon size={22} style={{ color: s.color }} />
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">{s.label}</span>
              </button>
            ))}
          </div>

          {/* QR code */}
          <div className="flex items-center gap-4 pt-1">
            <div ref={qrRef} className="bg-white p-2.5 rounded-xl shrink-0" data-testid="share-store-qr">
              <QRCodeCanvas value={link} size={96} bgColor="#ffffff" fgColor="#0F0817" level="M" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase text-xs tracking-wide">Scan to join</div>
              <p className="text-xs text-[#A1A1AA] mt-1 mb-2">Add it to packaging, posters or your bio.</p>
              <button
                onClick={downloadQr}
                data-testid="share-store-qr-download-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#00F0FF] hover:text-[#00F0FF] transition text-xs font-bold uppercase tracking-wider"
              >
                <Download size={13} /> Save QR
              </button>
            </div>
          </div>
        </div>

        {toast && (
          <div className="px-5 pb-4 -mt-1">
            <div data-testid="share-store-toast" className="text-center text-xs font-bold uppercase tracking-widest text-[#D4FF00] bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-full py-2 px-3">
              {toast}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
